import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Download, FileText, Trash2, ArrowRight, Building, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const QuotesPage = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    notes: ''
  });
  const [items, setItems] = useState([
    { description: '', quantity: 1, unit_price: 0, total: 0 }
  ]);

  useEffect(() => {
    fetchQuotes();
    fetchClients();
  }, []);

  const fetchQuotes = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
      console.error('Error fetching quotes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClient(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_name: client.client_name,
        client_email: client.billing_details.billing_email,
        client_phone: client.billing_details.billing_phone,
        client_address: client.billing_details.billing_address
      });
      setSelectedSite(''); // Reset site selection
    }
  };

  const handleSiteSelect = (siteIndex) => {
    setSelectedSite(siteIndex);
    const client = clients.find(c => c.id === selectedClient);
    if (client && client.sites[siteIndex]) {
      const site = client.sites[siteIndex];
      setFormData({
        ...formData,
        client_address: site.site_address
      });
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * 0.1; // 10% tax
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const { subtotal, tax, total } = calculateTotals();

    try {
      await axios.post(
        `${API}/quotes`,
        {
          ...formData,
          items,
          subtotal,
          tax,
          total
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Quote created successfully!');
      setIsDialogOpen(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to create quote');
      console.error('Error creating quote:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      client_address: '',
      notes: ''
    });
    setItems([{ description: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const downloadPDF = async (quoteId, quoteNumber) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${quoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download PDF');
      console.error('Error downloading PDF:', error);
    }
  };

  const convertToInvoice = async (quoteId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/quotes/${quoteId}/convert-to-invoice`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Quote converted to invoice!');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to convert to invoice');
      console.error('Error converting to invoice:', error);
    }
  };

  const deleteQuote = async (quoteId) => {
    const token = localStorage.getItem('token');
    if (!confirm('Are you sure you want to delete this quote?')) return;

    try {
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Quote deleted successfully!');
      fetchQuotes();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete quote';
      toast.error(errorMsg);
      console.error('Error deleting quote:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const { subtotal, tax, total } = calculateTotals();

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="quotes-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Quotes</h1>
              <p className="text-gray-400">Create and manage client quotes</p>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-quote-btn" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0">
                <Plus className="w-5 h-5 mr-2" />
                Create Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">Create New Quote</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Client Selector */}
                {clients.length > 0 && (
                  <div className="bg-white/5 p-4 rounded-lg space-y-4">
                    <div className="flex items-center gap-2 text-orange-400 mb-2">
                      <Building className="w-5 h-5" />
                      <span className="font-medium">Select Existing Client (Optional)</span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Client</Label>
                        <Select value={selectedClient} onValueChange={handleClientSelect}>
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue placeholder="Select a client..." />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-white/20">
                            <SelectItem value="none" className="text-white hover:bg-white/10">-- New Client --</SelectItem>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id} className="text-white hover:bg-white/10">
                                {client.client_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedClient && clients.find(c => c.id === selectedClient)?.sites?.length > 0 && (
                        <div>
                          <Label className="text-gray-300">Site</Label>
                          <Select value={selectedSite} onValueChange={handleSiteSelect}>
                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                              <SelectValue placeholder="Select a site..." />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-white/20">
                              <SelectItem value="billing" className="text-white hover:bg-white/10">-- Billing Address --</SelectItem>
                              {clients.find(c => c.id === selectedClient).sites.map((site, index) => (
                                <SelectItem key={index} value={index.toString()} className="text-white hover:bg-white/10">
                                  {site.site_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client_name" className="text-gray-300">Client Name *</Label>
                    <Input
                      id="client_name"
                      data-testid="quote-client-name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="client_email" className="text-gray-300">Client Email</Label>
                    <Input
                      id="client_email"
                      data-testid="quote-client-email"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      required
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="client_phone" className="text-gray-300">Client Phone</Label>
                    <Input
                      id="client_phone"
                      data-testid="quote-client-phone"
                      type="tel"
                      value={formData.client_phone}
                      onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      required
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="client_address" className="text-gray-300">Client Address</Label>
                    <Input
                      id="client_address"
                      data-testid="quote-client-address"
                      value={formData.client_address}
                      onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                      required
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-gray-300">Items</Label>
                    <Button type="button" onClick={addItem} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end bg-white/5 p-4 rounded-lg">
                        <div className="col-span-5">
                          <Label className="text-gray-400 text-xs">Description</Label>
                          <Input
                            data-testid={`item-description-${index}`}
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            required
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Label className="text-gray-400 text-xs">Quantity</Label>
                          <Input
                            data-testid={`item-quantity-${index}`}
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value))}
                            required
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Label className="text-gray-400 text-xs">Unit Price</Label>
                          <Input
                            data-testid={`item-price-${index}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value))}
                            required
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Label className="text-gray-400 text-xs">Total</Label>
                          <Input
                            value={`$${item.total.toFixed(2)}`}
                            disabled
                            className="bg-white/5 border-white/20 text-white"
                          />
                        </div>
                        
                        <div className="col-span-1 flex justify-end">
                          {items.length > 1 && (
                            <Button
                              data-testid={`remove-item-${index}`}
                              type="button"
                              onClick={() => removeItem(index)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white/5 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal:</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Tax (10%):</span>
                    <span>${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-white text-lg font-bold border-t border-white/20 pt-2">
                    <span>Total:</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes" className="text-gray-300">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="quote-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="bg-white/10 border-white/20 text-white resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" data-testid="submit-quote-btn" className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0">
                    Create Quote
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-white/20 text-white hover:bg-white/10">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Loading quotes...</p>
          </div>
        ) : quotes.length === 0 ? (
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No quotes yet. Create your first quote!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {quotes.map((quote, index) => (
              <Card key={quote.id} data-testid={`quote-card-${index}`} className="bg-white/5 backdrop-blur-sm border-white/10 hover:border-orange-500/30">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl text-white mb-2">
                        {quote.quote_number} - {quote.client_name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                        <span>{quote.client_email}</span>
                        <span>{quote.client_phone}</span>
                        <span>{formatDate(quote.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        quote.status === 'converted' ? 'bg-green-500/20 text-green-400' :
                        quote.status === 'accepted' ? 'bg-blue-500/20 text-blue-400' :
                        quote.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {quote.status.toUpperCase()}
                      </span>
                      <span className="text-2xl font-bold text-white">${quote.total.toFixed(2)}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      data-testid={`download-pdf-${index}`}
                      onClick={() => downloadPDF(quote.id, quote.quote_number)}
                      size="sm"
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                    
                    {quote.status !== 'converted' && (
                      <Button
                        data-testid={`convert-to-invoice-${index}`}
                        onClick={() => convertToInvoice(quote.id)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white border-0"
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Convert to Invoice
                      </Button>
                    )}
                    
                    <Button
                      data-testid={`delete-quote-${index}`}
                      onClick={() => deleteQuote(quote.id)}
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default QuotesPage;
